import React, { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router';

import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import { Autocomplete, Checkbox, Chip, TextField } from '@mui/material';
import debounce from 'lodash/debounce';
// eslint-disable-next-line
import get from 'lodash/get';

import { CircularProgress } from './styled';

export interface SearchSelectProps {
	value?: any;
	onChange?: (value: any) => void;
	error?: string;
	loading?: boolean;
	label: string;
	data: any[];
	itemLabelKey: string;
	onSearch?: (value: string) => void;
	multiple?: boolean;
	disabled?: boolean;
	prepareOption?: (option: any) => any; // @TODO: Is it possible typing
	placeholder?: string;
	getOptionLink?: (option: any) => string;
	isOptionEqualToValue?: (option: any, value: any) => boolean;
	disableClearable: boolean;
	getOptionLabel?: (option: any) => string;
	isChecked?: boolean;
	withFirstOption?: any;
	required?: boolean;
}

function SearchSelect({
	value,
	onChange,
	error,
	loading,
	label,
	data = [],
	itemLabelKey,
	onSearch,
	multiple,
	disabled,
	placeholder,
	getOptionLink,
	isOptionEqualToValue = (option, value) =>
		option === value || option?.id === value?.id || option.id === value, // @TODO: Govnokod
	getOptionLabel,
	isChecked = false,
	withFirstOption,
	...props
}: SearchSelectProps) {
	const navigate = useNavigate();

	const handleSearch = (value: string) => {
		onSearch && onSearch(value);
	};

	// @TODO: errors, touched
	useEffect(() => {
		handleSearch('');
	}, []);

	const handleTextChange = debounce((e) => {
		handleSearch(e.target.value);
	}, 500);

	const getOptionLabelFinal = getOptionLabel || ((option: any) => get(option, itemLabelKey));

	const handleBlur = () => {
		// Reset filtered options when input loses focus
		handleSearch('');
	};

	const modifiedData = useMemo(
		() => (withFirstOption ? [withFirstOption, ...data] : data),
		[withFirstOption, data],
	);

	const handleChange = (_: any, newValue: any) => {
		if (multiple) {
			const isSelectAll = newValue.some((item: any) => item.id === 'all');
			if (isSelectAll && withFirstOption) {
				onChange && onChange(data.length === value?.length ? [] : data);
			} else {
				onChange && onChange(newValue);
			}
		} else {
			onChange && onChange(newValue);
		}
	};

	return (
		<Autocomplete
			value={value}
			onChange={handleChange}
			fullWidth
			multiple={multiple}
			options={modifiedData}
			loading={loading}
			getOptionLabel={getOptionLabelFinal}
			getOptionKey={(option) => option?.id || get(option, itemLabelKey)}
			filterOptions={onSearch ? (option) => option : undefined}
			isOptionEqualToValue={isOptionEqualToValue}
			disabled={disabled}
			renderInput={(params) => (
				<TextField
					{...params}
					label={label}
					placeholder={placeholder}
					error={!!error}
					helperText={error || ''}
					InputProps={{
						...params.InputProps,
						endAdornment: (
							<>
								{!!loading && <CircularProgress />}
								{isChecked && (
									<CheckCircleOutlinedIcon
										sx={{
											color: 'green',
											ml: 1,
											fontSize: 20,
										}}
									/>
								)}
								{params.InputProps.endAdornment}
							</>
						),
					}}
					onChange={handleTextChange}
					onBlur={handleBlur}
					required={props?.required}
				/>
			)}
			renderTags={(value, getTagProps, ownerState) =>
				value.map((item, index) => {
					const option = ownerState.options.find((option) => isOptionEqualToValue(option, item));
					const { key, ...tagProps } = getTagProps({ index });
					return (
						<Chip
							onClick={getOptionLink ? () => navigate(getOptionLink(option)) : undefined}
							label={getOptionLabelFinal(option)}
							{...tagProps}
							key={key}
						/>
					);
				})}
			renderOption={(optionProps, option, { selected }) => (
				<li {...optionProps}>
					{multiple && <Checkbox checked={selected} size="small" sx={{ padding: 0, mr: '9px' }} />}
					{getOptionLabelFinal(option)}
				</li>
			)}
			{...props}
		/>
	);
}

export default SearchSelect;
